/**
 * Client-side canonical image rotation for the Item Editor.
 *
 * Order: decode → EXIF-normalize (createImageBitmap / draw) → apply user
 * clockwise 90° quarters → export Blob at full pixel resolution.
 * Never a CSS-only transform — the File that uploads is already oriented.
 */

function loadImageElement(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法读取图片'))
    }
    img.src = url
  })
}

async function drawNormalized(file: File): Promise<HTMLCanvasElement> {
  // Prefer createImageBitmap which applies EXIF orientation in modern browsers.
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions)
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas unavailable')
    ctx.drawImage(bitmap, 0, 0)
    bitmap.close()
    return canvas
  } catch {
    const img = await loadImageElement(file)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas unavailable')
    ctx.drawImage(img, 0, 0)
    return canvas
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('导出图片失败'))
        else resolve(blob)
      },
      type,
      quality,
    )
  })
}

/** Rotate `file` clockwise by `quarters * 90°` and return a new File. */
export async function rotateImageFile(file: File, quarters: number = 1): Promise<File> {
  const q = ((quarters % 4) + 4) % 4
  if (q === 0) return file

  const source = await drawNormalized(file)
  const out = document.createElement('canvas')
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('Canvas unavailable')

  if (q % 2 === 1) {
    out.width = source.height
    out.height = source.width
  } else {
    out.width = source.width
    out.height = source.height
  }

  ctx.translate(out.width / 2, out.height / 2)
  ctx.rotate((q * Math.PI) / 2)
  ctx.drawImage(source, -source.width / 2, -source.height / 2)

  const preferPng = file.type === 'image/png' || file.type === 'image/webp'
  const mime = preferPng ? 'image/png' : 'image/jpeg'
  // quality 0.95 — do not silently crush the original for rotation alone.
  const blob = await canvasToBlob(out, mime, preferPng ? undefined : 0.95)
  const base = file.name.replace(/\.[^.]+$/, '') || 'image'
  const ext = preferPng ? 'png' : 'jpg'
  return new File([blob], `${base}.${ext}`, { type: mime, lastModified: Date.now() })
}
